import React, {
    useEffect, useState, useCallback, useRef,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';
import { isNonMmlAdmin } from '../utils/roles';
import MemberSearchField, { memberDisplayName } from '../Components/MemberSearchField';
import NotAvailable from '../Components/NotAvailable';
import {
    STATUS_OPTIONS, GRANULARITY_OPTIONS, NO_ROWS, formatDollars, formatNumber, formatPct, commissaryLabel, stateLabel,
    scopeParams, granularityParam, dayRangeError, periodLabel, hourLabel, DAY_NAMES, csvCell,
    usePaged, TablePager, StatCard, TimeCharts, TENDER_OPTIONS,
    ProductCodeCell, productScope, formatProductCode,
} from './LoyaltyInsights';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// One product, or a group of products, across members' receipts
// (somo-circle1-api-admin#194, API: somo-circle1-api#787): how much is
// bought, by how many members, where and when. CircleOne-only, like Receipt
// Insights: the API routes are behind requireNonMmlAdmin.

const UNKNOWN = 'unknown';
// The API's limits.
const MAX_PRODUCTS = 50;
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DELAY_MS = 300;

export const EMPTY_FILTERS = {
    from: '', to: '', commissaryBaseId: '', state: '', member: null, status: 'accepted', granularity: 'month', tender: '', products: [], codes: [],
};

// The chosen products (somo-circle1-api#800): codes [{ code, name,
// codeType, displayCode }] for products with a printed UPC/PLU, and
// products (names) for ones without. A hand-off from before #800 has no
// codes, and one from before displayCode has only code and name.
export const selectedItems = (filters) => [
    ...(filters.codes || []).map((c) => ({
        product: c.name, productCode: c.code, codeType: c.codeType || null, displayCode: c.displayCode || null,
    })),
    ...filters.products.map((product) => ({ product, productCode: null })),
];
const itemKey = (item) => item.productCode || `name:${item.product}`;
const asSelection = (items) => ({
    products: items.filter((i) => !i.productCode).map((i) => i.product),
    codes: items.filter((i) => i.productCode).map((i) => ({
        code: i.productCode, name: i.product, codeType: i.codeType, displayCode: i.displayCode,
    })),
});
// A chosen product's receipts: by code, or every line printed with the name
// (a chosen name matches coded lines too, unlike a product row with no code).
const selectionScope = (item) => (item.productCode ? { productCode: item.productCode } : { product: item.product });

// The products/insights query string: product (a name) or code, once per product.
export const productInsightsQuery = (filters) => {
    const params = new URLSearchParams({ ...scopeParams(filters), status: filters.status, ...granularityParam(filters) });
    filters.products.forEach((product) => params.append('product', product));
    (filters.codes || []).forEach((c) => params.append('code', c.code));
    return params.toString();
};

const PRODUCT_COLUMNS = [
    ['Receipts', 'receiptCount'],
    ['Buyers', 'buyerCount'],
    ['Units', 'totalQuantity'],
    ['Product spend', 'totalSpend'],
    ['Basket spend', 'basketSpend'],
    ['Share of basket %', 'shareOfBasketPct'],
];

export const buildProductInsightsCsv = (data) => {
    const rows = [['Products', ...data.products]];
    if (data.codes?.length) rows.push(['SKU / UPC', ...data.codes]);
    rows.push(['Repeat buyers', data.totals.repeatBuyerCount]);
    rows.push(['Repeat buyer %', data.totals.repeatBuyerPct]);
    rows.push(['Avg days between purchases', data.totals.avgDaysBetweenPurchases]);
    // An API from before somo-circle1-api#799 sends no tender.
    if (data.tender) {
        const t = data.tender;
        rows.push(['Buyers who paid with SNAP', t.snapBuyerCount, 'SNAP buyer %', t.snapBuyerPct]);
        rows.push(['Buyers who paid with WIC', t.wicBuyerCount, 'WIC buyer %', t.wicBuyerPct]);
        rows.push(['Units on SNAP receipts', t.snapReceiptUnits, 'Spend on SNAP receipts', t.snapReceiptSpend]);
        rows.push(['Units on WIC receipts', t.wicReceiptUnits, 'Spend on WIC receipts', t.wicReceiptSpend]);
        rows.push(['Units bought with WIC', t.wicItemUnits, 'Spend bought with WIC', t.wicItemSpend]);
    }
    rows.push([]);
    rows.push(['Section', 'Group', ...PRODUCT_COLUMNS.map(([label]) => label), 'SKU / UPC']);
    const groupRow = (section, group, row) => [section, group, ...PRODUCT_COLUMNS.map(([, key]) => row[key])];
    rows.push(groupRow('Totals', 'All', data.totals));
    data.perProduct.forEach((row) => rows.push([...groupRow('Product', row.product, row), row.productCode]));
    data.byPeriod.forEach((row) => rows.push(groupRow(periodLabel(data.granularity), row.period, row)));
    data.byHour.forEach((row) => rows.push(groupRow('Hour', hourLabel(row.hour), row)));
    data.byDayOfWeek.forEach((row) => rows.push(groupRow('Day of week', DAY_NAMES[row.dayOfWeek - 1], row)));
    data.byCommissary.forEach((row) => rows.push(groupRow('Commissary', commissaryLabel(row), row)));
    data.byState.forEach((row) => rows.push(groupRow('State', stateLabel(row.state), row)));
    return rows.map((row) => row.map(csvCell).join(',')).join('\n');
};

async function fetchJson(url, fallbackMessage) {
    const res = await authManager.authenticatedFetch(url);
    // A gateway timeout's body is HTML or empty, not JSON.
    const body = await res.json().catch(() => null);
    if (!res.ok || body?.status !== 'success') throw new Error(body?.message || fallbackMessage);
    return body.data;
}

// Search-as-you-type over the products on receipts, by name or (digits) by
// UPC/PLU. Picking one hands { product, productCode } to onAdd; products
// already chosen aren't offered again.
function ProductPicker({ selected, onAdd, disabled }) {
    const [text, setText] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const latestRef = useRef(0);
    useEffect(() => () => { latestRef.current += 1; }, []);

    useEffect(() => {
        const search = text.trim();
        const requestId = ++latestRef.current;
        if (search.length < MIN_SEARCH_LENGTH) {
            setResults([]);
            setSearching(false);
            return undefined;
        }
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const data = await fetchJson(
                    `${Base_Url}/admin/loyalty/products?${new URLSearchParams({ search })}`,
                    'Failed to search products.',
                );
                if (requestId === latestRef.current) setResults(data.products);
            } catch (err) {
                if (requestId === latestRef.current) toast.error(err.message);
            } finally {
                if (requestId === latestRef.current) setSearching(false);
            }
        }, SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [text]);

    const chosen = new Set(selected.map(itemKey));
    // A chosen name already covers its coded lines, so the code rows printed
    // with it aren't offered either. An API from before #800 sends no
    // productCode, so those are names.
    const offered = results.filter((r) => !chosen.has(itemKey({ product: r.product, productCode: r.productCode || null }))
        && !chosen.has(itemKey({ product: r.product, productCode: null })));
    const full = selected.length >= MAX_PRODUCTS;

    return (
        <div>
            <label htmlFor="product-search">Add a product</label>
            <input
                id="product-search"
                type="search"
                className="form-control"
                placeholder={full ? `At most ${MAX_PRODUCTS} products` : 'Type at least 2 letters or digits, e.g. MILK or a UPC'}
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={disabled || full}
                autoComplete="off"
            />
            {searching && <small className="text-muted">Searching…</small>}
            {!searching && text.trim().length >= MIN_SEARCH_LENGTH && offered.length === 0 && (
                <small className="text-muted">No other products match.</small>
            )}
            {offered.length > 0 && (
                <ul className="list-group mt-1" style={{ maxHeight: '240px', overflowY: 'auto' }} data-testid="product-search-results">
                    {offered.map((r) => (
                        <li key={itemKey(r)} className="list-group-item p-0">
                            <button
                                type="button"
                                className="btn btn-link btn-block text-left d-flex justify-content-between"
                                onClick={() => {
                                    onAdd({
                                        product: r.product,
                                        productCode: r.productCode || null,
                                        codeType: r.codeType || null,
                                        displayCode: r.displayCode || null,
                                    });
                                    setText('');
                                }}
                            >
                                <span>
                                    {r.product}
                                    {r.productCode && (
                                        <small className="text-muted ml-2">
                                            {`${r.codeType === 'plu' ? 'PLU ' : ''}${formatProductCode(r)}`}
                                        </small>
                                    )}
                                </span>
                                <small className="text-muted">{`${formatNumber(r.receiptCount)} receipts`}</small>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// showCode adds the SKU / UPC column, for the per-product table.
function ProductGroupTable({
    title, groupHeader, rows, rowKey, rowLabel, linkState, testId, showCode = false,
}) {
    const pages = usePaged(rows);
    return (
        <div className="card mb-3" data-testid={testId}>
            <div className="card-header"><h3 className="card-title">{title}</h3></div>
            <div className="card-body table-responsive p-0">
                <table className="table table-striped mb-0 txt_lft">
                    <thead>
                        <tr>
                            <th>{groupHeader}</th>
                            {showCode && <th>SKU / UPC</th>}
                            <th>Receipts</th>
                            <th>Buyers</th>
                            <th>Units</th>
                            <th>Product spend</th>
                            <th>Share of basket</th>
                            {linkState && <th aria-label="Actions" />}
                        </tr>
                    </thead>
                    <tbody>
                        {pages.pageRows.map((row) => (
                            <tr key={rowKey(row)}>
                                <td className="fwt_600">{rowLabel(row)}</td>
                                {showCode && <td><ProductCodeCell row={row} /></td>}
                                <td>{formatNumber(row.receiptCount)}</td>
                                <td>{formatNumber(row.buyerCount)}</td>
                                <td>{formatNumber(row.totalQuantity)}</td>
                                <td>{formatDollars(row.totalSpend)}</td>
                                <td>{formatPct(row.shareOfBasketPct)}</td>
                                {linkState && (
                                    <td className="text-right">
                                        <Link to="/loyaltyReceipts" state={linkState(row)}>Receipts</Link>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <TablePager {...pages} label={title} />
        </div>
    );
}

function LoyaltyProductInsights() {
    const location = useLocation();
    // Opened from Receipt Insights' Top products, or back from the receipts
    // queue: both hand over the filters and products to show.
    const [initialFilters] = useState(() => ({ ...EMPTY_FILTERS, ...location.state?.restoreFilters }));
    const [allowed, setAllowed] = useState(null); // null = still checking
    const [filters, setFilters] = useState(initialFilters);
    const [applied, setApplied] = useState(initialFilters);
    // The last filters and products asked for, which are ahead of applied
    // while a load runs or after one fails. Adding or removing a product and
    // Retry build on these, so a failed or slow load never drops a change.
    const [requested, setRequested] = useState(initialFilters);
    const [memberFieldKey, setMemberFieldKey] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [options, setOptions] = useState({ commissaries: [], states: [] });

    const latestRequestRef = useRef(0);
    useEffect(() => () => { latestRequestRef.current += 1; }, []);
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    const load = useCallback(async (next) => {
        const requestId = ++latestRequestRef.current;
        setRequested(next);
        if (!selectedItems(next).length) {
            // Nothing to show until a product is chosen.
            setApplied(next);
            setData(null);
            setLoadError(null);
            setLoading(false);
            navigateRef.current('.', { replace: true, state: { restoreFilters: next } });
            return;
        }
        setLoading(true);
        try {
            const result = await fetchJson(
                `${Base_Url}/admin/loyalty/products/insights?${productInsightsQuery(next)}`,
                'Failed to load product insights.',
            );
            if (requestId !== latestRequestRef.current) return;
            setApplied(next);
            setData(result);
            setLoadError(null);
            // Keep this history entry on what's shown, so a refresh or browser
            // Back restores it.
            navigateRef.current('.', { replace: true, state: { restoreFilters: next } });
        } catch (err) {
            if (requestId !== latestRequestRef.current) return;
            console.error('Error fetching product insights:', err);
            setLoadError(err.message);
        } finally {
            if (requestId === latestRequestRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        (async () => {
            const currentUser = await authManager.validateUser().catch(() => null);
            if (!isNonMmlAdmin(currentUser)) {
                setAllowed(false);
                return;
            }
            setAllowed(true);
            load(initialFilters);
            // Commissary/state dropdown choices, the same as Receipt Insights.
            fetchJson(
                `${Base_Url}/admin/loyalty/receipts/insights?${new URLSearchParams({ status: 'all', topProducts: 0, topMembers: 0 })}`,
                'Failed to load filter options.',
            )
                .then((all) => setOptions({
                    commissaries: all.byCommissary.filter((row) => row.commissaryBaseId),
                    states: all.byState.map((row) => row.state).filter(Boolean).sort(),
                }))
                .catch((err) => console.error('Error loading product insights filter options:', err));
        })();
    }, [load, initialFilters]);

    const setFilter = (field) => (e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }));

    const handleApply = (e) => {
        e.preventDefault();
        if (filters.from && filters.to && filters.from > filters.to) {
            toast.error('The "from" date must be on or before the "to" date.');
            return;
        }
        const dayError = dayRangeError(filters);
        if (dayError) {
            toast.error(dayError);
            return;
        }
        load({ ...filters, products: requested.products, codes: requested.codes || [] });
    };

    const handleReset = () => {
        const next = { ...EMPTY_FILTERS, products: requested.products, codes: requested.codes || [] };
        setFilters(next);
        setMemberFieldKey((k) => k + 1);
        load(next);
    };

    // Adding or removing a product reloads at once, with the filters last
    // asked for (anything typed but not applied is kept in the form).
    const setItems = (items) => load({ ...requested, ...asSelection(items) });

    // Opens the receipts queue on the receipts behind a figure. The queue
    // filters by one product, so this is only offered for a single product.
    const receiptsLinkState = (item, extraScope = {}, label = '') => ({
        restoreFilters: {
            page: 1,
            status: applied.status,
            source: 'all',
            name: '',
            email: '',
            scope: { ...scopeParams(applied), ...selectionScope(item), ...extraScope },
            scopeLabel: label || `Product: ${item.product}`,
            // For the queue's "Back to Product Insights".
            productReturn: applied,
        },
    });
    const appliedItems = selectedItems(applied);
    const requestedItems = selectedItems(requested);
    const single = appliedItems.length === 1 ? appliedItems[0] : null;

    const handleExport = () => {
        if (!data) return;
        const blob = new Blob([buildProductInsightsCsv(data)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `product-insights-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Hooks, so above the early return.
    const periodPages = usePaged(data?.byPeriod || NO_ROWS);

    if (allowed === false) return <NotAvailable />;

    const totals = data?.totals;
    // Receipt Insights has the same filters, minus the products.
    const insightsFilters = { ...applied };
    delete insightsFilters.products;
    delete insightsFilters.codes;
    const maxPeriodSpend = Math.max(0, ...(data?.byPeriod || []).map((p) => p.totalSpend || 0));
    const trendLabel = periodLabel(data?.granularity);

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Product Insights</h1>
                        </div>
                        <div className="col-sm-6 text-sm-right">
                            <Link
                                to="/loyaltyInsights"
                                state={{ restoreFilters: insightsFilters }}
                                className="btn btn-outline-secondary mr-2"
                            >
                                <i className="fa-solid fa-arrow-left-long" /> Receipt Insights
                            </Link>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleExport}
                                disabled={!data || loading}
                            >
                                <i className="fa-solid fa-download" /> Export CSV
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-2">
                                    <ProductPicker
                                        selected={requestedItems}
                                        onAdd={(item) => setItems([...requestedItems, item])}
                                        disabled={allowed !== true || loading}
                                    />
                                </div>
                                <div className="col-md-6 mb-2" data-testid="selected-products">
                                    <label className="d-block">Products</label>
                                    {requestedItems.length === 0 && <span className="text-muted">None chosen yet.</span>}
                                    {requestedItems.map((item) => (
                                        <span key={itemKey(item)} className="badge badge-light border mr-2 mb-2 p-2" style={{ fontSize: '0.85rem' }}>
                                            {item.product}
                                            {item.productCode && (
                                                <span className="text-muted text-monospace ml-1">
                                                    {`${item.codeType === 'plu' ? 'PLU ' : ''}${formatProductCode(item)}`}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 ml-2"
                                                aria-label={`Remove ${item.product}`}
                                                onClick={() => setItems(requestedItems.filter((i) => itemKey(i) !== itemKey(item)))}
                                                disabled={loading}
                                            >
                                                <i className="fa-solid fa-xmark" aria-hidden="true" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card mb-3">
                        <form className="card-body" onSubmit={handleApply}>
                            <div className="row align-items-end">
                                <div className="col-md-2">
                                    <label htmlFor="product-from">From</label>
                                    <input id="product-from" type="date" className="form-control" value={filters.from} onChange={setFilter('from')} />
                                </div>
                                <div className="col-md-2">
                                    <label htmlFor="product-to">To</label>
                                    <input id="product-to" type="date" className="form-control" value={filters.to} onChange={setFilter('to')} />
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="product-commissary">Commissary</label>
                                    <select id="product-commissary" className="form-control" value={filters.commissaryBaseId} onChange={setFilter('commissaryBaseId')}>
                                        <option value="">All commissaries</option>
                                        {options.commissaries.map((row) => (
                                            <option key={row.commissaryBaseId} value={row.commissaryBaseId}>{commissaryLabel(row)}</option>
                                        ))}
                                        <option value={UNKNOWN}>Unknown (not matched)</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label htmlFor="product-state">State</label>
                                    <select id="product-state" className="form-control" value={filters.state} onChange={setFilter('state')}>
                                        <option value="">All states</option>
                                        {options.states.map((s) => <option key={s} value={s}>{s}</option>)}
                                        <option value={UNKNOWN}>Unknown</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="product-status">Status</label>
                                    <select id="product-status" className="form-control" value={filters.status} onChange={setFilter('status')}>
                                        {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="row align-items-start mt-3">
                                <div className="col-md-2">
                                    <label htmlFor="product-granularity">Trend by</label>
                                    <select id="product-granularity" className="form-control" value={filters.granularity} onChange={setFilter('granularity')}>
                                        {GRANULARITY_OPTIONS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label htmlFor="product-tender">Paid with</label>
                                    <select id="product-tender" className="form-control" value={filters.tender} onChange={setFilter('tender')}>
                                        {TENDER_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-5">
                                    <MemberSearchField
                                        key={memberFieldKey}
                                        initialMember={filters.member}
                                        required={false}
                                        onSelect={(member) => setFilters((prev) => ({ ...prev, member }))}
                                    />
                                </div>
                                <div className="col-md-3 d-flex" style={{ gap: '8px' }}>
                                    <button
                                        type="submit"
                                        className="btn btn-primary flex-fill"
                                        style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                        disabled={loading}
                                    >
                                        Apply
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary flex-fill" onClick={handleReset} disabled={loading}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                            <small className="text-muted d-block">
                                Products are grouped by SKU/UPC where one is printed, shown under the name they&apos;re most often printed as.
                                Product spend is the printed line totals; share of basket is
                                that as a % of the spend on the receipts the products were bought on.
                            </small>
                        </form>
                    </div>

                    {requestedItems.length === 0 && (
                        <div className="card mb-3">
                            <div className="card-body text-center py-5">
                                <h5>Add a product to see how much of it members buy.</h5>
                                <p className="text-muted mb-0">Add more than one to see them together, e.g. a brand&apos;s products.</p>
                            </div>
                        </div>
                    )}

                    {loading && (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    )}

                    {!loading && loadError && (
                        <div className="alert alert-danger d-flex flex-wrap align-items-center justify-content-between" role="alert" style={{ gap: '8px' }}>
                            <span>
                                {data
                                    ? `Couldn't apply these changes: ${loadError} Showing the previous results.`
                                    : loadError}
                            </span>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => load(requested)}>
                                Retry
                            </button>
                        </div>
                    )}

                    {!loading && data && totals.receiptCount === 0 && (
                        <div className="card mb-3">
                            <div className="card-body text-center py-5">
                                <h5>No receipts with these products match these filters.</h5>
                            </div>
                        </div>
                    )}

                    {!loading && data && totals.receiptCount > 0 && (
                        <>
                            <div className="row" data-testid="product-totals">
                                <StatCard label="Units" value={formatNumber(totals.totalQuantity)} />
                                <StatCard label="Product spend" value={formatDollars(totals.totalSpend)} />
                                <StatCard
                                    label="Receipts"
                                    value={formatNumber(totals.receiptCount)}
                                    linkState={single ? receiptsLinkState(single) : null}
                                    title={single ? 'View these receipts' : undefined}
                                />
                                <StatCard label="Buyers" value={formatNumber(totals.buyerCount)} hint="members" />
                                <StatCard
                                    label="Repeat buyers"
                                    value={formatNumber(totals.repeatBuyerCount)}
                                    hint={`${formatPct(totals.repeatBuyerPct)} of buyers bought more than once`}
                                />
                                <StatCard label="Avg days between purchases" value={formatNumber(totals.avgDaysBetweenPurchases)} hint="for repeat buyers" />
                                <StatCard
                                    label="Share of basket"
                                    value={formatPct(totals.shareOfBasketPct)}
                                    hint={`of ${formatDollars(totals.basketSpend)} spent on these receipts`}
                                />
                            </div>

                            {/* SNAP / WIC buyers (somo-circle1-api#799). Totals only. */}
                            {data.tender && (
                                <div className="card mb-3" data-testid="product-tender">
                                    <div className="card-header">
                                        <h3 className="card-title">SNAP &amp; WIC</h3>
                                        <small className="text-muted ml-2">buyers who paid with SNAP or WIC on a receipt with these products</small>
                                    </div>
                                    <div className="card-body pb-0">
                                        <div className="row">
                                            <StatCard
                                                label="Paid with SNAP"
                                                value={formatNumber(data.tender.snapBuyerCount)}
                                                hint={`${formatPct(data.tender.snapBuyerPct)} of buyers`}
                                            />
                                            <StatCard
                                                label="Paid with WIC"
                                                value={formatNumber(data.tender.wicBuyerCount)}
                                                hint={`${formatPct(data.tender.wicBuyerPct)} of buyers`}
                                            />
                                            <StatCard
                                                label="On SNAP receipts"
                                                value={`${formatNumber(data.tender.snapReceiptUnits)} units`}
                                                hint={formatDollars(data.tender.snapReceiptSpend)}
                                            />
                                            <StatCard
                                                label="On WIC receipts"
                                                value={`${formatNumber(data.tender.wicReceiptUnits)} units`}
                                                hint={formatDollars(data.tender.wicReceiptSpend)}
                                            />
                                            <StatCard
                                                label="Bought with WIC benefits"
                                                value={`${formatNumber(data.tender.wicItemUnits)} units`}
                                                hint={`${formatDollars(data.tender.wicItemSpend)}, items printed "WIC …"`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {data.perProduct.length > 1 && (
                                <ProductGroupTable
                                    testId="product-by-product"
                                    title="By product"
                                    groupHeader="Product"
                                    rows={data.perProduct}
                                    rowKey={itemKey}
                                    showCode
                                    rowLabel={(row) => row.product}
                                    linkState={(row) => receiptsLinkState(row, productScope(row))}
                                />
                            )}

                            <div className="card mb-3">
                                <div className="card-header"><h3 className="card-title">{`By ${trendLabel.toLowerCase()}`}</h3></div>
                                <div className="card-body table-responsive p-0">
                                    <table className="table table-striped mb-0 txt_lft">
                                        <thead>
                                            <tr>
                                                <th>{trendLabel}</th>
                                                <th>Receipts</th>
                                                <th>Buyers</th>
                                                <th>Units</th>
                                                <th style={{ width: '40%' }}>Product spend</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {periodPages.pageRows.map((p) => (
                                                <tr key={p.period}>
                                                    <td>{p.period}</td>
                                                    <td>{formatNumber(p.receiptCount)}</td>
                                                    <td>{formatNumber(p.buyerCount)}</td>
                                                    <td>{formatNumber(p.totalQuantity)}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                                            <div
                                                                aria-hidden="true"
                                                                style={{
                                                                    height: '10px',
                                                                    borderRadius: '2px',
                                                                    background: 'var(--primary)',
                                                                    width: `${maxPeriodSpend ? Math.round(((p.totalSpend || 0) / maxPeriodSpend) * 70) : 0}%`,
                                                                }}
                                                            />
                                                            <span>{formatDollars(p.totalSpend)}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <TablePager {...periodPages} label={`By ${trendLabel.toLowerCase()}`} />
                            </div>

                            <TimeCharts data={data} totalReceipts={totals.receiptCount} />

                            <div className="row">
                                <div className="col-xl-7">
                                    <ProductGroupTable
                                        title="By commissary"
                                        groupHeader="Commissary"
                                        rows={data.byCommissary}
                                        rowKey={(row) => row.commissaryBaseId || UNKNOWN}
                                        rowLabel={commissaryLabel}
                                        linkState={single ? (row) => receiptsLinkState(
                                            single,
                                            { commissaryBaseId: row.commissaryBaseId || UNKNOWN },
                                            `${single.product} at ${commissaryLabel(row)}`,
                                        ) : null}
                                    />
                                </div>
                                <div className="col-xl-5">
                                    <ProductGroupTable
                                        title="By state"
                                        groupHeader="State"
                                        rows={data.byState}
                                        rowKey={(row) => row.state || UNKNOWN}
                                        rowLabel={(row) => stateLabel(row.state)}
                                        linkState={single ? (row) => receiptsLinkState(
                                            single,
                                            { state: row.state || UNKNOWN },
                                            `${single.product} in ${stateLabel(row.state)}`,
                                        ) : null}
                                    />
                                </div>
                            </div>
                            {applied.member && (
                                <small className="text-muted d-block mb-3">
                                    Showing receipts for {memberDisplayName(applied.member)} ({applied.member.email}).
                                </small>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default LoyaltyProductInsights;
